import React, { useMemo } from 'react';
import { Box, Card, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
  Cell,
  LabelList,
} from 'recharts';

import {
  RADIUS,
  makeFormatters,
  fmtPct,
  fmtX,
  deltaProps,
  useSeries,
  Stat,
  Section,
  ChartCard,
  Callout,
  useAxis,
  ChartTip,
  legendStyle,
  BAR_END,
  StatementTable,
  DashboardShell,
} from './financeDashboardKit';
import {
  COMPANY,
  YEARS,
  YEAR_META,
  INCOME,
  DEPRECIATION,
  BALANCE_SHEET,
  CASH_FLOW,
  NOTE_YEARS,
  FUNDING,
  WORKING_CAPITAL,
  RELATED_PARTY_YEARS,
  RELATED_PARTY,
  HMRC_CLAIM,
  SUBSIDIARIES,
  PEOPLE,
  GEOGRAPHY,
  OWED_TO_GROUP_FY25,
  EMISSIONS_FY25,
} from '../constants/tauraniFinancials';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'profitability', label: 'Profitability' },
  { key: 'group', label: 'Funding & Group Links' },
  { key: 'cash', label: 'Cash & Working Capital' },
  { key: 'statements', label: 'Statements' },
];

// Source figures are whole pounds; the toggle shows GBP million or thousand.
const UNITS = {
  mn: { label: 'GBP Mn', factor: 1e-6, digits: 1, suffix: 'Mn' },
  k: { label: "GBP '000", factor: 1e-3, digits: 0, suffix: 'K' },
};

const { fmtAmt, fmtAxis, fmtCell } = makeFormatters(UNITS, 'GBP');

// ---------- derivations ----------

function buildAnalytics() {
  const i = INCOME;
  const b = BALANCE_SHEET;
  const n = YEARS.length;
  const map = (fn) => YEARS.map((_, k) => fn(k));
  const ebitda = map((k) => i.operatingProfit[k] + DEPRECIATION[k]);
  const relatedPurchaseShare = RELATED_PARTY_YEARS.map((y, k) => {
    const idx = YEARS.indexOf(y);
    return (RELATED_PARTY.purchasesFromGroup[k] / -i.costOfSales[idx]) * 100;
  });
  const L = n - 1;

  // Funding structure at 30 June 2025: what pays for the balance sheet.
  const funding = NOTE_YEARS.map((y, k) => ({
    period: y,
    'Shareholders’ equity': YEARS.indexOf(y) >= 0 ? b.netAssets[YEARS.indexOf(y)] : null,
    'Loans from Taurani group': FUNDING.relatedPartyLoans[k],
    'Owed to sister companies (trade)': FUNDING.relatedPartyTradeCreditors[k],
    'Invoice discounting': FUNDING.invoiceDiscounting[k],
    'Bank & hire purchase': FUNDING.bankLoansOverdrafts[k] + FUNDING.hirePurchase[k],
  }));
  const groupFunding = FUNDING.relatedPartyLoans.map((v, k) => v + FUNDING.relatedPartyTradeCreditors[k]);

  return {
    latest: L,
    prior: L - 1,
    ebitda,
    grossMargin: map((k) => (i.grossProfit[k] / i.turnover[k]) * 100),
    operatingMargin: map((k) => (i.operatingProfit[k] / i.turnover[k]) * 100),
    adminRatio: map((k) => (-i.adminExpenses[k] / i.turnover[k]) * 100),
    stockDays: map((k) => (b.stocks[k] / -i.costOfSales[k]) * 365),
    debtorDays: map((k) => (b.debtors[k] / i.turnover[k]) * 365),
    relatedPurchaseShare,
    groupFunding,
    groupFundingToEquity: groupFunding.map((v, k) => v / b.netAssets[YEARS.indexOf(NOTE_YEARS[k])]),
    performance: map((k) => ({
      period: YEARS[k],
      turnover: i.turnover[k],
      grossProfit: i.grossProfit[k],
    })),
    margins: map((k) => ({
      period: YEARS[k],
      gross: (i.grossProfit[k] / i.turnover[k]) * 100,
      operating: (i.operatingProfit[k] / i.turnover[k]) * 100,
    })),
    profit: map((k) => ({ period: YEARS[k], pat: i.profitAfterTax[k], ebitda: ebitda[k] })),
    netAssets: map((k) => ({ period: YEARS[k], netAssets: b.netAssets[k] })),
    cashFlows: map((k) => ({
      period: YEARS[k],
      operating: CASH_FLOW.netOperating[k],
      investing: CASH_FLOW.netInvesting[k],
      financing: CASH_FLOW.netFinancing[k],
    })),
    workingCapital: map((k) => ({
      period: YEARS[k],
      stockDays: (b.stocks[k] / -i.costOfSales[k]) * 365,
      debtorDays: (b.debtors[k] / i.turnover[k]) * 365,
    })),
    relatedPurchases: RELATED_PARTY_YEARS.map((y, k) => ({
      period: y,
      fromGroup: RELATED_PARTY.purchasesFromGroup[k],
      other: -i.costOfSales[YEARS.indexOf(y)] - RELATED_PARTY.purchasesFromGroup[k],
    })),
    funding,
  };
}

// ---------- tab views ----------

const OverviewTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const i = INCOME;
  const b = BALANCE_SHEET;
  const { latest: L, prior: P } = a;
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];

  return (
    <>
      <Callout tone="neutral">
        <b>Scope:</b> these are the audited accounts of {COMPANY.legalName}, the UK &amp; Ireland arm of the Taurani
        Holdings group (Parkersteel, Brisko Scaffolding, Total Safety Solutions). The wider group in the UAE, Oman,
        Australia, India and the USA publishes no accounts, so it is not shown here. Financial years end 30 June.
      </Callout>

      <Section title="FY2025 at a glance" caption="Year ended 30 June 2025 against FY2024.">
        <Stat hero label="Turnover" value={money(i.turnover[L])} {...deltaProps(i.turnover[L], i.turnover[P])} sub="YoY" />
        <Stat hero label="Gross profit" value={money(i.grossProfit[L])} {...deltaProps(i.grossProfit[L], i.grossProfit[P])} sub={`${fmtPct(a.grossMargin[L])} margin`} tone="positive" />
        <Stat hero label="EBITDA" value={money(a.ebitda[L])} {...deltaProps(a.ebitda[L], a.ebitda[P])} sub="operating loss + depreciation" tone="warning" title="Operating result plus depreciation and amortisation (from the cash-flow note)" />
        <Stat hero label="Loss after tax" value={money(i.profitAfterTax[L])} sub={`FY24 ${fmtAmt(i.profitAfterTax[P], unit)}`} tone="negative" />
        <Stat hero label="Net assets" value={money(b.netAssets[L])} {...deltaProps(b.netAssets[L], b.netAssets[P])} sub="YoY" tone="warning" />
        <Stat hero label="Cash from operations" value={money(CASH_FLOW.cashFromOperations[L])} sub={`FY24 ${fmtAmt(CASH_FLOW.cashFromOperations[P], unit)}`} tone="negative" />
      </Section>

      <Callout tone="warning">
        <b>A boom and a bust, not a trend.</b> Turnover and profit peaked in FY2022 with steel prices
        ({fmtAmt(i.turnover[1], 'mn')} turnover, {fmtAmt(i.profitAfterTax[1], 'mn')} profit), then fell three years running.
        Almost all of the FY2024–25 losses sit in <b>Parkersteel</b> ({fmtAmt(SUBSIDIARIES[0].pat[1], 'mn')} in FY2025).
        The auditor&rsquo;s opinion is clean, with no going-concern warning.
      </Callout>

      <Section title="Five-year trend">
        <ChartCard title="Turnover and gross profit" caption={`${UNITS[unit].label}, years ended 30 June.`}>
          <ResponsiveContainer>
            <BarChart data={a.performance} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="turnover" name="Turnover" fill={c[0]} radius={BAR_END} />
              <Bar dataKey="grossProfit" name="Gross profit" fill={c[2]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Margins" caption="Gross and operating result as a share of turnover.">
          <ResponsiveContainer>
            <LineChart data={a.margins} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeOpacity={0.6} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="gross" name="Gross margin" stroke={c[2]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="operating" name="Operating margin" stroke={c[1]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Profit after tax and EBITDA" caption={UNITS[unit].label}>
          <ResponsiveContainer>
            <BarChart data={a.profit} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeOpacity={0.6} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="ebitda" name="EBITDA" fill={c[3]} radius={BAR_END} />
              <Bar dataKey="pat" name="Profit after tax" fill={c[0]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Net assets" caption={`${UNITS[unit].label}. Shareholders' equity at 30 June.`}>
          <ResponsiveContainer>
            <LineChart data={a.netAssets} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} domain={[0, 'auto']} />
              <ChartTip formatter={tipMoney} />
              <Line type="monotone" dataKey="netAssets" name="Net assets" stroke={c[0]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const ProfitabilityTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const L = a.latest;
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];
  const subsidiaryRows = SUBSIDIARIES.filter((s) => s.business !== 'Dormant').map((s) => ({
    name: s.name.replace(' Ltd', ''),
    FY24: s.pat[0],
    FY25: s.pat[1],
  }));
  const turnoverPerHead = PEOPLE.employees.map((e, k) => INCOME.turnover[YEARS.indexOf(NOTE_YEARS[k])] / e);

  return (
    <>
      <Section title="Where the loss comes from" caption="Profit or loss for the year of each subsidiary, from note 11.">
        <ChartCard title="Subsidiary results" caption={`${UNITS[unit].label}. Parkersteel accounts for almost the whole group loss.`} full height={260}>
          <ResponsiveContainer>
            <BarChart data={subsidiaryRows} layout="vertical" barGap={2} margin={{ left: 8, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} horizontal={false} />
              <XAxis type="number" {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <YAxis type="category" dataKey="name" {...axis} width={210} />
              <ReferenceLine x={0} stroke={theme.palette.text.secondary} strokeOpacity={0.6} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="FY24" name="FY2024" fill={c[3]} radius={[0, 4, 4, 0]} />
              <Bar dataKey="FY25" name="FY2025" fill={c[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section title="Cost base" caption="Overheads stayed near GBP 24 Mn while turnover fell from GBP 189 Mn to GBP 137 Mn.">
        <Stat label="Administrative expenses FY25" value={fmtAmt(-INCOME.adminExpenses[L], unit)} sub={`${fmtPct(a.adminRatio[L])} of turnover (FY22 ${fmtPct(a.adminRatio[1])})`} tone="warning" />
        <Stat label="Gross margin FY25" value={fmtPct(a.grossMargin[L])} sub={`FY22 peak ${fmtPct(a.grossMargin[1])}`} tone="positive" title="The strategic report quotes 17.8%; the income statement gives 18.0%" />
        <Stat label="Employees FY25" value={PEOPLE.employees[1]} {...deltaProps(PEOPLE.employees[1], PEOPLE.employees[0], false)} sub="average headcount" />
        <Stat label="Staff costs FY25" value={fmtAmt(PEOPLE.staffCosts[1], unit)} {...deltaProps(PEOPLE.staffCosts[1], PEOPLE.staffCosts[0], false)} />
        <Stat label="Turnover per employee" value={fmtAmt(turnoverPerHead[1], 'k')} {...deltaProps(turnoverPerHead[1], turnoverPerHead[0])} />
        <Stat label="Finance costs FY25" value={fmtAmt(-INCOME.interestPayable[L], unit)} {...deltaProps(-INCOME.interestPayable[L], -INCOME.interestPayable[L - 1], false)} tone="negative" />
      </Section>

      <Section>
        <ChartCard title="Overheads against turnover" caption="Administrative expenses as a share of turnover.">
          <ResponsiveContainer>
            <BarChart data={YEARS.map((y, k) => ({ period: y, ratio: a.adminRatio[k] }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v) => [fmtPct(v), 'Admin / turnover']} />
              <Bar dataKey="ratio" name="Admin / turnover" fill={c[1]} radius={BAR_END}>
                <LabelList dataKey="ratio" position="top" formatter={(v) => fmtPct(v)} style={{ fill: theme.palette.text.secondary, fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <Card elevation={0} sx={{ flex: '1 1 440px', minWidth: 280, p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}`, overflowX: 'auto' }}>
          <Box sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Turnover by market</Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{UNITS[unit].label}</TableCell>
                {NOTE_YEARS.map((y) => (
                  <TableCell key={y} align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{y}</TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Share FY25</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {GEOGRAPHY.map((g) => (
                <TableRow key={g.region}>
                  <TableCell sx={{ fontSize: 12.5 }}>{g.region}</TableCell>
                  {g.values.map((v, k) => (
                    <TableCell key={k} align="right" sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{fmtCell(v, unit)}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize: 12.5 }}>{fmtPct((g.values[1] / INCOME.turnover[L]) * 100)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1.5 }}>
            FY2025 emissions: {EMISSIONS_FY25.total.toLocaleString()} tonnes CO₂e (scope 1: {EMISSIONS_FY25.scope1.toLocaleString()}, scope 2: {EMISSIONS_FY25.scope2}).
          </Box>
        </Card>
      </Section>
    </>
  );
};

const GroupTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];
  const fundingKeys = ['Shareholders’ equity', 'Loans from Taurani group', 'Owed to sister companies (trade)', 'Invoice discounting', 'Bank & hire purchase'];

  return (
    <>
      <Section title="How dependent is the UK business on the Taurani group?">
        <Stat hero label="Bought from sister companies FY25" value={fmtAmt(RELATED_PARTY.purchasesFromGroup[3], unit)} sub={`${fmtPct(a.relatedPurchaseShare[3])} of cost of sales`} tone="warning" />
        <Stat hero label="Group funding at 30 Jun 25" value={fmtAmt(a.groupFunding[1], unit)} sub="loans + trade balances owed to the group" tone="warning" />
        <Stat hero label="Group funding / net assets" value={fmtX(a.groupFundingToEquity[1])} sub={`FY24 ${fmtX(a.groupFundingToEquity[0])}`} tone="negative" />
        <Stat hero label="Invoice discounting" value={fmtAmt(FUNDING.invoiceDiscounting[1], unit)} {...deltaProps(FUNDING.invoiceDiscounting[1], FUNDING.invoiceDiscounting[0], false)} sub="secured on receivables" />
      </Section>

      <Callout tone="warning">
        The banking facilities (invoice discounting and overdraft) are <b>guaranteed by the wider Taurani group</b>.
        The {fmtAmt(FUNDING.relatedPartyLoans[1], 'mn')} parent loan has no fixed repayment date and needs at least 13 months&rsquo;
        notice. Separately, HMRC has assessed Parkersteel for {fmtAmt(HMRC_CLAIM, 'mn')} of retrospective safeguard duty and import
        VAT (a post-Brexit, industry-wide issue), which the company is defending.
      </Callout>

      <Section>
        <ChartCard title="Purchases from Taurani sister companies" caption={`${UNITS[unit].label}. Cost of sales split into group and third-party supply.`}>
          <ResponsiveContainer>
            <BarChart data={a.relatedPurchases}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="fromGroup" name="From Taurani group" stackId="s" fill={c[1]} stroke={theme.palette.background.paper} strokeWidth={2} />
              <Bar dataKey="other" name="Other cost of sales" stackId="s" fill={c[0]} stroke={theme.palette.background.paper} strokeWidth={2} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="How the business is funded" caption={`${UNITS[unit].label}, at 30 June. Group funding has grown while equity shrank.`}>
          <ResponsiveContainer>
            <BarChart data={a.funding}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              {fundingKeys.map((k, idx) => (
                <Bar key={k} dataKey={k} stackId="f" fill={c[idx]} stroke={theme.palette.background.paper} strokeWidth={2} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Owed to Taurani companies — 30 June 2025" caption={`${UNITS[unit].label}, from the related-party note.`} full height={280}>
          <ResponsiveContainer>
            <BarChart data={OWED_TO_GROUP_FY25} layout="vertical" margin={{ left: 8, right: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} horizontal={false} />
              <XAxis type="number" {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <YAxis type="category" dataKey="name" {...axis} width={260} />
              <ChartTip formatter={(v) => [fmtAmt(v, unit), 'Owed']} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {OWED_TO_GROUP_FY25.map((_, k) => (
                  <Cell key={k} fill={k === 0 ? c[1] : c[0]} />
                ))}
                <LabelList dataKey="value" position="right" formatter={(v) => fmtCell(v, unit)} style={{ fill: theme.palette.text.secondary, fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const CashTab = ({ a, unit }) => {
  const axis = useAxis();
  const c = useSeries();
  const theme = useTheme();
  const L = a.latest;
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];
  const wc = WORKING_CAPITAL;

  return (
    <>
      <Section title="Cash and working capital" caption="Stock days use cost of sales; debtor days use total debtors over turnover.">
        <Stat hero label="Net cash from operations FY25" value={fmtAmt(CASH_FLOW.netOperating[L], unit)} sub={`FY24 ${fmtAmt(CASH_FLOW.netOperating[L - 1], Math.abs(CASH_FLOW.netOperating[L - 1]) < 1e6 ? "k" : unit)}`} tone="negative" />
        <Stat hero label="Cash at 30 Jun 25" value={fmtAmt(BALANCE_SHEET.cash[L], unit)} {...deltaProps(BALANCE_SHEET.cash[L], BALANCE_SHEET.cash[L - 1])} sub="YoY" tone="warning" />
        <Stat hero label="Stock days" value={`${Math.round(a.stockDays[L])} days`} sub={`FY24 ${Math.round(a.stockDays[L - 1])} days`} tone="warning" />
        <Stat hero label="Debtor days" value={`${Math.round(a.debtorDays[L])} days`} sub={`FY24 ${Math.round(a.debtorDays[L - 1])} days`} tone="positive" />
        <Stat label="Stock FY25" value={fmtAmt(BALANCE_SHEET.stocks[L], unit)} sub={`raw ${fmtAmt(wc.rawMaterials[1], 'mn')} · finished ${fmtAmt(wc.finishedGoods[1], 'mn')}`} />
        <Stat label="Trade debtors FY25" value={fmtAmt(wc.tradeDebtors[1], unit)} {...deltaProps(wc.tradeDebtors[1], wc.tradeDebtors[0], false)} />
        <Stat label="Capital expenditure FY25" value={fmtAmt(-(CASH_FLOW.capexTangible[L] + CASH_FLOW.capexIntangible[L]), unit)} sub="tangible + intangible" />
      </Section>

      <Section>
        <ChartCard title="Cash flows" caption={`${UNITS[unit].label}. Operating cash turned negative in FY2024 and FY2025; financing (mainly group loans) filled the gap.`}>
          <ResponsiveContainer>
            <BarChart data={a.cashFlows} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAxis(v, unit)} />
              <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeOpacity={0.6} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="operating" name="Operating" fill={c[0]} radius={BAR_END} />
              <Bar dataKey="investing" name="Investing" fill={c[1]} radius={BAR_END} />
              <Bar dataKey="financing" name="Financing" fill={c[2]} radius={BAR_END} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Working-capital days" caption="Days of cost of sales held in stock, and days of turnover owed by customers.">
          <ResponsiveContainer>
            <LineChart data={a.workingCapital} margin={{ top: 16, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="period" {...axis} />
              <YAxis {...axis} domain={[0, 'auto']} />
              <ChartTip formatter={(v, n) => [`${Math.round(v)} days`, n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="stockDays" name="Stock days" stroke={c[3]} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="debtorDays" name="Debtor days" stroke={c[0]} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

// ---------- statements ----------

const IS_LAYOUT = [
  ['turnover', 'Turnover', true],
  ['costOfSales', 'Cost of sales'],
  ['grossProfit', 'Gross profit', true],
  ['distributionCosts', 'Distribution costs'],
  ['adminExpenses', 'Administrative expenses'],
  ['otherOperatingIncome', 'Other operating income'],
  ['operatingProfit', 'Operating profit / (loss)', true],
  ['interestReceivable', 'Interest receivable'],
  ['interestPayable', 'Interest payable'],
  ['profitBeforeTax', 'Profit / (loss) before tax', true],
  ['tax', 'Tax'],
  ['profitAfterTax', 'Profit / (loss) for the year', true],
  ['toOwners', 'Attributable to owners of the parent'],
  ['toNci', 'Attributable to non-controlling interests'],
];

const BS_LAYOUT = [
  ['intangibles', 'Intangible assets'],
  ['tangibles', 'Tangible assets'],
  ['investmentProperty', 'Investment property'],
  ['fixedAssets', 'Fixed assets', true],
  ['stocks', 'Stocks'],
  ['debtors', 'Debtors'],
  ['currentInvestments', 'Current asset investments'],
  ['cash', 'Cash at bank and in hand'],
  ['currentAssets', 'Current assets', true],
  ['creditorsWithinYear', 'Creditors due within one year'],
  ['netCurrentAssets', 'Net current assets', true],
  ['creditorsAfterYear', 'Creditors due after more than one year'],
  ['provisions', 'Provisions for liabilities'],
  ['netAssets', 'NET ASSETS', true],
  ['shareCapital', 'Called up share capital'],
  ['sharePremium', 'Share premium'],
  ['retainedEarnings', 'Retained earnings'],
  ['shareholdersFunds', "Shareholders' funds", true],
  ['nci', 'Non-controlling interests'],
];

const CF_LAYOUT = [
  ['cashFromOperations', 'Cash generated from operations'],
  ['netOperating', 'Net cash from operating activities', true],
  ['capexTangible', 'Purchase of tangible fixed assets'],
  ['capexIntangible', 'Purchase of intangible fixed assets'],
  ['netInvesting', 'Net cash from investing activities', true],
  ['netFinancing', 'Net cash from financing activities', true],
  ['netChange', 'Increase / (decrease) in cash', true],
];

const StatementsTab = () => {
  const theme = useTheme();
  return (
    <>
      <Section>
        <StatementTable title="Consolidated income statement" caption="Years ended 30 June, as printed (latest restated figure for each year)." periods={YEARS} layout={IS_LAYOUT} values={INCOME} unitLabel="GBP" />
        <StatementTable title="Consolidated balance sheet" caption="At 30 June." periods={YEARS} layout={BS_LAYOUT} values={BALANCE_SHEET} unitLabel="GBP" />
        <StatementTable
          title="Consolidated cash flow statement — summary"
          caption="FY2024 is the restated comparative: GBP 1.8 Mn of related-party loans moved from operating to financing in the FY2025 accounts."
          periods={YEARS}
          layout={CF_LAYOUT}
          values={CASH_FLOW}
          unitLabel="GBP"
        />
      </Section>
      <Section title="Sources">
        <Card elevation={0} sx={{ flex: '1 1 100%', p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}` }}>
          {Object.entries(YEAR_META).map(([p, m]) => (
            <Box key={p} sx={{ fontSize: 12.5, py: 0.3 }}>
              <b>{p}</b> · year ended {m.endedOn} · {m.source}
            </Box>
          ))}
          <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1.5 }}>
            {COMPANY.legalName} ({COMPANY.registration}) · subsidiary of {COMPANY.parent} · {COMPANY.basis}.
            Figures as filed at Companies House, in GBP. EBITDA, margins, working-capital days and group-dependency
            ratios are calculated here and are not taken from the accounts.
          </Box>
        </Card>
      </Section>
    </>
  );
};

const TAB_VIEWS = {
  overview: OverviewTab,
  profitability: ProfitabilityTab,
  group: GroupTab,
  cash: CashTab,
  statements: StatementsTab,
};

// ---------- shell ----------

const TauraniDashboard = ({ open, onClose }) => {
  // Static snapshot of filed accounts: a pure derivation, no fetch.
  const analytics = useMemo(() => buildAnalytics(), []);
  return (
    <DashboardShell
      open={open}
      onClose={onClose}
      title="Taurani Holdings — UK & Ireland Financial Overview"
      subtitle={`${COMPANY.legalName} · ${COMPANY.basis} · FY2021–FY2025 (years to 30 June)`}
      tabs={TABS}
      views={TAB_VIEWS}
      units={UNITS}
      defaultUnit="mn"
      analytics={analytics}
    />
  );
};

export default TauraniDashboard;
